<script lang="ts">
    let submitted = $state(false);
    let submitting = $state(false);
    let error = $state('');
    let phone = $state('');

    function formatPhone(value: string): string {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        if (digits.length < 4) return digits;
        if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    function handlePhoneInput(e: Event) {
        const input = e.target as HTMLInputElement;
        phone = formatPhone(input.value);
        input.value = phone;
    }

    async function handleSubmit(e: SubmitEvent) {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        submitting = true;
        error = '';

        try {
            const response = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                body: new FormData(form),
            });

            const data = await response.json();

            if (data.success) {
                submitted = true;
                form.reset();
            } else {
                error = 'Something went wrong. Please try again.';
            }
        } catch {
            error = 'Unable to submit. Please check your connection and try again.';
        } finally {
            submitting = false;
        }
    }
</script>

<section id="contact" class="border-t border-slate-200 bg-slate-50 px-6 py-20">
    <div
        class="mx-auto grid max-w-[1140px] grid-cols-1 items-start gap-16 lg:grid-cols-[1fr_1.1fr]"
    >
        <!-- Left column - info -->
        <div>
            <h2 class="mb-4 text-3xl font-extrabold -tracking-[0.03em] text-slate-900 lg:text-4xl">
                Ready to take the first step?
            </h2>
            <p class="mb-8 text-lg leading-relaxed text-slate-500">
                It starts with a simple conversation. Share a little about your situation, and we'll
                let you know how we can help &mdash; clearly, honestly, and quickly.
            </p>

            <ol class="flex list-none flex-col gap-5">
                <li class="flex items-start gap-4">
                    <span
                        class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-xs font-bold text-white"
                    >
                        1
                    </span>
                    <div>
                        <h4 class="text-[0.95rem] font-bold text-slate-800">
                            Tell us your situation
                        </h4>
                        <p class="text-sm text-slate-400">
                            Fill out the form and a team member will reach out.
                        </p>
                    </div>
                </li>
                <li class="flex items-start gap-4">
                    <span
                        class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-xs font-bold text-white"
                    >
                        2
                    </span>
                    <div>
                        <h4 class="text-[0.95rem] font-bold text-slate-800">We review your case</h4>
                        <p class="text-sm text-slate-400">
                            We look at the estate details to see what you qualify for.
                        </p>
                    </div>
                </li>
                <li class="flex items-start gap-4">
                    <span
                        class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-xs font-bold text-white"
                    >
                        3
                    </span>
                    <div>
                        <h4 class="text-[0.95rem] font-bold text-slate-800">Receive your funds</h4>
                        <p class="text-sm text-slate-400">
                            Once approved, money is deposited directly to you.
                        </p>
                    </div>
                </li>
            </ol>
        </div>

        <!-- Right column - form -->
        <form
            class="rounded-2xl border border-slate-200 bg-white p-10 shadow-sm shadow-slate-900/5"
            onsubmit={handleSubmit}
        >
            <input
                type="hidden"
                name="access_key"
                value={import.meta.env.VITE_WEB3FORMS_ACCESS_KEY}
            />

            <!-- Honeypot spam protection -->
            <input type="checkbox" name="botcheck" class="hidden" />

            <div class="mb-7">
                <h3 class="mb-1 text-xl font-bold text-slate-900">See what you qualify for</h3>
                <p class="text-sm text-slate-400">No obligation. No pressure. Just answers.</p>
            </div>

            {#if submitted}
                <div class="rounded-xl bg-teal-50 p-6 text-center">
                    <p class="font-semibold text-teal-600">
                        Thank you! We'll be in touch within 1 business day.
                    </p>
                </div>
            {:else}
                <div class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <label
                            for="firstName"
                            class="mb-1 block text-xs font-semibold text-slate-700"
                        >
                            First Name
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            name="first_name"
                            placeholder="First name"
                            required
                            class="w-full rounded-xl border-[1.5px] border-slate-200 bg-white px-3.5 py-3 text-[0.92rem] text-slate-800 transition-all outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                        />
                    </div>
                    <div>
                        <label
                            for="lastName"
                            class="mb-1 block text-xs font-semibold text-slate-700"
                        >
                            Last Name
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            name="last_name"
                            placeholder="Last name"
                            required
                            class="w-full rounded-xl border-[1.5px] border-slate-200 bg-white px-3.5 py-3 text-[0.92rem] text-slate-800 transition-all outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                        />
                    </div>
                </div>

                <div class="mb-4">
                    <label for="email" class="mb-1 block text-xs font-semibold text-slate-700">
                        Email
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="you@email.com"
                        required
                        class="w-full rounded-xl border-[1.5px] border-slate-200 bg-white px-3.5 py-3 text-[0.92rem] text-slate-800 transition-all outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                    />
                </div>

                <div class="mb-4">
                    <label for="phone" class="mb-1 block text-xs font-semibold text-slate-700">
                        Phone
                    </label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="(555) 123-4567"
                        value={phone}
                        oninput={handlePhoneInput}
                        class="w-full rounded-xl border-[1.5px] border-slate-200 bg-white px-3.5 py-3 text-[0.92rem] text-slate-800 transition-all outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                    />
                </div>

                <input type="hidden" name="state" value="Florida" />

                <div class="mb-4">
                    <label for="message" class="mb-1 block text-xs font-semibold text-slate-700">
                        Tell us more (optional)
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        placeholder="Anything you'd like us to know about your situation"
                        rows="3"
                        class="w-full resize-y rounded-xl border-[1.5px] border-slate-200 bg-white px-3.5 py-3 text-[0.92rem] text-slate-800 transition-all outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                    ></textarea>
                </div>

                {#if error}
                    <p class="mb-3 text-sm text-red-600">{error}</p>
                {/if}

                <button
                    type="submit"
                    disabled={submitting}
                    class="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3.5 text-[0.95rem] font-semibold text-white transition-all hover:-translate-y-px hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/30 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                    {submitting ? 'Submitting...' : 'Submit Inquiry'}
                    {#if !submitting}
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                        >
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    {/if}
                </button>

                <p class="mt-4 text-center text-xs leading-relaxed text-slate-400">
                    Your information is secure and will never be shared with third parties.<br />
                    We'll respond within 1 business day.
                </p>
            {/if}
        </form>
    </div>
</section>
